/**
 * Lazily loads CodeMirror 6 — same reasoning as `mermaidRender.ts`'s rule-5
 * dynamic import, applied to a second large library. Statically importing
 * `@codemirror/view` + `language` + `commands` pushed the initial JS chunk
 * to 142.39 KB gzipped, within 8 KB of SPEC.md §11's 150 KB budget, before
 * issue #12/#13 add more CodeMirror code and before any of M4/M5/M6 lands.
 * Dynamic import brought the real entry chunk back to 52.92 KB gzipped
 * (confirmed via the file `dist/index.html` actually references — Vite
 * names several of the resulting lazy chunks "index-*" too, so check the
 * HTML's own script tag, not just the largest "index" filename in the build
 * output). Same caching-promise shape as `mermaidRender.ts`'s `getMermaid`.
 *
 * `mermaidStreamParser`/`foldMermaidSubgraph` (issue #10) are safe to import
 * statically at the top of this file, unlike the CodeMirror packages
 * themselves — `mermaidLanguage.ts` only ever imports CodeMirror's *types*
 * (erased at build), never its runtime, so pulling it in doesn't reintroduce
 * the bundle problem this file exists to avoid.
 */
import type { EditorState, Extension } from '@codemirror/state';
import { mermaidStreamParser, foldMermaidSubgraph } from './mermaidLanguage';

let setupPromise: ReturnType<typeof loadSetup> | null = null;

// Design tokens, not hardcoded hex (CLAUDE.md rule 10) — CSS custom property
// references work fine as CodeMirror theme values, same as in a stylesheet.
async function loadSetup() {
  const [
    { EditorView, keymap, lineNumbers, highlightActiveLine, highlightActiveLineGutter, Decoration, GutterMarker, gutter },
    { bracketMatching, indentUnit, StreamLanguage, HighlightStyle, syntaxHighlighting, foldGutter, foldService },
    { defaultKeymap, history, historyKeymap, indentWithTab },
    { tags },
    { Compartment, StateEffect, StateField },
    { vim },
  ] = await Promise.all([
    import('@codemirror/view'),
    import('@codemirror/language'),
    import('@codemirror/commands'),
    import('@lezer/highlight'),
    import('@codemirror/state'),
    import('@replit/codemirror-vim'),
  ]);

  const mermaidLanguage = StreamLanguage.define(mermaidStreamParser);

  // SPEC.md §10, issue #36 (M6.9): the only wiring `Cmd/Ctrl+/` actually
  // needs. `@codemirror/commands`' own `defaultKeymap` (already spread into
  // the keymap extension below) binds `Mod-/` to `toggleComment` out of the
  // box — that command just no-ops without a language's `commentTokens`
  // language data to find. `mermaidStreamParser` (mermaidLanguage.ts)
  // already tokenizes a leading `%%` as `comment`; this is what tells
  // `toggleComment`/`toggleLineComment` that `%%` is the line-comment token
  // to insert or strip, so a comment/uncomment toggle and a doubled or
  // missing `%%` are never a possibility.
  const mermaidLanguageData = mermaidLanguage.data.of({ commentTokens: { line: '%%' } });

  // Every token type mermaidStreamParser (#10) can return — keyword,
  // operator, bracket, string, comment, meta — maps 1:1 onto a real
  // @lezer/highlight tag name, confirmed against the installed package
  // (no custom tokenTable needed). Colors are existing tokens.css variables,
  // not new hex (CLAUDE.md rule 10, this ticket's own acceptance criterion):
  // pink for keywords (the language's primary structural marker, same
  // register as the caret/primary-action uses of pink elsewhere), ink-4 for
  // brackets (matches its existing "line numbers, disabled" framing —
  // shape delimiters are punctuation, not content), green for strings
  // (label content), amber for frontmatter (a distinct block, not the
  // diagram body).
  //
  // Operators and comments both land on mute, distinguished by comments'
  // italic. This is the one unavoidable overlap in the six-category ask:
  // the palette has five non-default colors (pink/amber/green/mute/ink-4),
  // one short of six, and the sixth token color — text-ink — is the plain
  // fallback every untokenized character already renders in, so using it
  // for a real category would fail "distinct from plain text" instead.
  // Mute-plus-italic for comments is the most conventional pairing of the
  // two categories forced to share (quiet framing fits both, per SPEC.md §9).
  const mermaidHighlightStyle = HighlightStyle.define([
    { tag: tags.keyword, color: 'var(--color-pink)' },
    { tag: tags.operator, color: 'var(--color-mute)' },
    { tag: tags.bracket, color: 'var(--color-ink-4)' },
    { tag: tags.string, color: 'var(--color-green)' },
    { tag: tags.comment, color: 'var(--color-mute)', fontStyle: 'italic' },
    { tag: tags.meta, color: 'var(--color-amber)' },
  ]);

  // Issue #13: a red squiggly underline plus a gutter marker on the render
  // loop's error.line — a view onto render.ts's existing state, not a new
  // timing rule. Editor.tsx dispatches setErrorLine whenever the `error`
  // prop changes; App.tsx (M1, already tested) is what guarantees error is
  // written only by a completed failed render and cleared only by a
  // successful one, so that invariant doesn't need reproducing here.
  const setErrorLine = StateEffect.define<number | null>();

  const errorLineField = StateField.define<number | null>({
    create: () => null,
    update(value, tr) {
      for (const effect of tr.effects) {
        if (effect.is(setErrorLine)) return effect.value;
      }
      return value;
    },
  });

  // Bounds-checked rather than clamped: a line number past the end of the
  // current document (a possible, if unlikely, mismatch between what
  // Mermaid reported and what's on screen right now) should show nothing,
  // not a decoration on the wrong line — showing the wrong line would be a
  // quiet lie, in the same spirit as never paraphrasing Mermaid's own
  // message (SPEC.md §9). Only the line *number* is resolved here — the
  // gutter marker needs nothing more than that to place itself. The
  // underline needs the actual Line object too, for a reason specific to
  // it alone (see resolveErrorLineForUnderline below), so that check isn't
  // duplicated into this shared helper.
  function resolveErrorLineNumber(state: EditorState): number | null {
    const lineNumber = state.field(errorLineField);
    if (lineNumber === null || lineNumber < 1 || lineNumber > state.doc.lines) return null;
    return lineNumber;
  }

  // A blank line has no characters to underline, and Decoration.mark's
  // range() throws on a zero-length range (from === to) — this isn't a
  // cosmetic skip, it's what stops a genuine crash on an error reported
  // against an empty line. The gutter marker has no such constraint (it
  // marks a line position, not a text range), so it deliberately does *not*
  // share this check — it can and should still point at a blank line.
  function resolveErrorLineForUnderline(state: EditorState) {
    const lineNumber = resolveErrorLineNumber(state);
    if (lineNumber === null) return null;
    const line = state.doc.line(lineNumber);
    return line.length === 0 ? null : line;
  }

  const errorMarkDecoration = Decoration.mark({ class: 'cm-error-line' });

  const errorLineDecorations = EditorView.decorations.of((view) => {
    const line = resolveErrorLineForUnderline(view.state);
    return line ? Decoration.set([errorMarkDecoration.range(line.from, line.to)]) : Decoration.none;
  });

  class ErrorGutterMarker extends GutterMarker {
    elementClass = 'cm-error-gutter-marker';
  }
  const errorGutterMarker = new ErrorGutterMarker();
  // Named, not anonymous, so it reads sensibly in a stack trace. A blank
  // marker (no elementClass) reserves the gutter's width from the start, so
  // a marker appearing/disappearing never shifts the line content sideways
  // — the column itself never changes size, only its content.
  class ErrorGutterSpacerMarker extends GutterMarker {}
  const errorGutterSpacer = new ErrorGutterSpacerMarker();

  const errorGutter = gutter({
    class: 'cm-error-gutter',
    lineMarker(view, line) {
      const lineNumber = resolveErrorLineNumber(view.state);
      if (lineNumber === null) return null;
      const errorLine = view.state.doc.line(lineNumber);
      return line.from === errorLine.from ? errorGutterMarker : null;
    },
    lineMarkerChange: (update) => update.startState.field(errorLineField) !== update.state.field(errorLineField),
    initialSpacer: () => errorGutterSpacer,
  });

  const theme = EditorView.theme(
    {
      '&': {
        height: '100%',
        backgroundColor: 'transparent',
        color: 'var(--color-text-ink)',
        fontFamily: 'var(--font-mono)',
        fontSize: '0.875rem',
      },
      '.cm-scroller': {
        overflow: 'auto',
        fontFamily: 'inherit',
      },
      '.cm-content': {
        caretColor: 'var(--color-pink)',
        padding: '0 0.75rem 0.75rem',
      },
      '.cm-cursor, .cm-dropCursor': {
        borderLeftColor: 'var(--color-pink)',
      },
      '.cm-activeLine, .cm-activeLineGutter': {
        backgroundColor: 'var(--color-ink-3)',
      },
      '.cm-gutters': {
        backgroundColor: 'transparent',
        color: 'var(--color-ink-4)',
        border: 'none',
      },
      // CodeMirror's own default foldPlaceholder is a hardcoded light-gray
      // box (from its baseTheme) — wrong against the dark ink palette.
      '.cm-foldPlaceholder': {
        backgroundColor: 'var(--color-ink-3)',
        border: 'none',
        color: 'var(--color-mute)',
      },
      // Issue #13 — the squiggly underline on a failed render's error line.
      '.cm-error-line': {
        textDecoration: 'underline wavy var(--color-pink)',
        textDecorationSkipInk: 'none',
      },
      // A fixed-width gutter (via initialSpacer below) so the marker's
      // appearance/disappearance never shifts the line content sideways.
      '.cm-error-gutter': {
        width: '0.9rem',
      },
      '.cm-error-gutter-marker::before': {
        content: '""',
        display: 'block',
        width: '6px',
        height: '6px',
        margin: '0.5em auto 0',
        borderRadius: '50%',
        backgroundColor: 'var(--color-pink)',
      },
      // Replaces CodeMirror's own default dotted outline with the same ring
      // every other control in the app uses (index.css's :focus-visible rule).
      '&.cm-focused': {
        outline: '2px solid var(--color-pink)',
        outlineOffset: '2px',
      },
    },
    { dark: true },
  );

  // A Compartment (not a plain extension) so vim mode can be toggled at
  // runtime — VimModeToggle.tsx / the command palette's equivalent entry —
  // without recreating the whole EditorView, which would lose the undo
  // stack and cursor position. Starts empty regardless of the persisted
  // setting; Editor.tsx dispatches the initial reconfigure right after
  // construction, same pattern as setErrorLine's post-construction dispatch
  // below. Placed first in `extensions` so vim's own keymap (once
  // reconfigured in) takes precedence over defaultKeymap/historyKeymap.
  const vimModeCompartment = new Compartment();

  const extensions: Extension[] = [
    vimModeCompartment.of([]),
    mermaidLanguage,
    mermaidLanguageData,
    syntaxHighlighting(mermaidHighlightStyle),
    foldGutter(),
    foldService.of(foldMermaidSubgraph),
    errorLineField,
    errorLineDecorations,
    errorGutter,
    lineNumbers(),
    highlightActiveLine(),
    highlightActiveLineGutter(),
    bracketMatching(),
    history(),
    indentUnit.of('  '),
    keymap.of([indentWithTab, ...defaultKeymap, ...historyKeymap]),
    theme,
    // Word wrap off by default is CodeMirror's natural behaviour — no
    // EditorView.lineWrapping extension is added (SPEC.md §5).
  ];

  return { EditorView, extensions, setErrorLine, vimModeCompartment, vim };
}

export function getCodeMirrorSetup() {
  setupPromise ??= loadSetup();
  return setupPromise;
}
